import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [engagements, setEngagements] = useState<any[]>([]);
  const [engagementsLoading, setEngagementsLoading] = useState(false);
  const [engagementsError, setEngagementsError] = useState("");

  // Parse user only once on mount
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "null");
      setUser(parsed);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const fetchEngagements = async () => {
      if (!user || !user.actorId) return;
      setEngagementsLoading(true);
      setEngagementsError("");
      try {
        const token = localStorage.getItem("accessToken");
        const res = await fetch(
          buildApiUrl(`/api/service-engagements/client/${user.actorId}`),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok)
          throw new Error(`Failed to fetch engagements (${res.status})`);
        const data = await res.json();
        setEngagements((data.data && data.data.content) || []);
      } catch (err: any) {
        setEngagementsError(err.message || "Failed to load engagements");
      } finally {
        setEngagementsLoading(false);
      }
    };
    if (user) fetchEngagements();
  }, [user]);

  if (!user) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#dc3545",
          fontSize: 22,
        }}
      >
        User not found. Please log in again.
      </div>
    );
  }

  return (
    <div
      style={{
        background: "linear-gradient(120deg, #f8f8ff 0%, #e0e7ff 100%)",
        minHeight: "100vh",
        padding: 0,
      }}
    >
      <div
        style={{
          maxWidth: 500,
          margin: "48px auto 0 auto",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          padding: 40,
          textAlign: "center",
        }}
      >
        <img
          src={
            user.profileImageUrl ||
            "https://ui-avatars.com/api/?name=" +
              encodeURIComponent(user.userFirstName || user.firstName || "U") +
              "+" +
              encodeURIComponent(user.userLastName || user.lastName || "N") +
              "&background=6C63FF&color=fff&size=128"
          }
          alt="Profile"
          style={{
            width: 110,
            height: 110,
            borderRadius: "50%",
            objectFit: "cover",
            marginBottom: 18,
            border: "4px solid #6C63FF22",
          }}
        />
        <h2
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 32,
            marginBottom: 8,
          }}
        >
          {user.userFirstName || user.firstName}{" "}
          {user.userLastName || user.lastName}
        </h2>
        <div style={{ color: "#888", fontSize: 18, marginBottom: 16 }}>
          {user.userEmail || user.email}
        </div>
        {user.city && (
          <div style={{ color: "#555", fontSize: 16, marginBottom: 8 }}>
            City: {user.city}
          </div>
        )}
        {user.state && (
          <div style={{ color: "#555", fontSize: 16, marginBottom: 8 }}>
            State: {user.state}
          </div>
        )}
        {user.country && (
          <div style={{ color: "#555", fontSize: 16, marginBottom: 8 }}>
            Country: {user.country}
          </div>
        )}
        {user.companyName && (
          <div style={{ color: "#555", fontSize: 16, marginBottom: 8 }}>
            Company: {user.companyName}
          </div>
        )}
        <button
          style={{
            background: "#6C63FF",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "12px 32px",
            fontWeight: 700,
            fontSize: 18,
            marginTop: 24,
            cursor: "pointer",
          }}
          onClick={() => navigate("/edit-profile")}
        >
          Edit Profile
        </button>
      </div>

      {/* Engagements Section */}
      <div
        style={{
          maxWidth: 700,
          margin: "32px auto 0 auto",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          padding: 32,
        }}
      >
        <h3
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 26,
            marginBottom: 18,
          }}
        >
          My Engagements
        </h3>
        {engagementsLoading ? (
          <div style={{ textAlign: "center", color: "#6C63FF", padding: 24 }}>
            <span style={{ fontSize: 28 }} role="img" aria-label="Loading">
              ⏳
            </span>{" "}
            Loading engagements...
          </div>
        ) : engagementsError ? (
          <div style={{ textAlign: "center", color: "#dc3545", padding: 24 }}>
            <span style={{ fontSize: 28 }} role="img" aria-label="Error">
              ❌
            </span>{" "}
            {engagementsError}
          </div>
        ) : engagements.length === 0 ? (
          <div style={{ textAlign: "center", color: "#888", padding: 24 }}>
            <span
              style={{ fontSize: 28 }}
              role="img"
              aria-label="No engagements"
            >
              😕
            </span>{" "}
            No engagements found.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {engagements.map((eng: any) => (
              <div
                key={eng.id}
                style={{
                  background: "#f8f8ff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px #e0e7ff",
                  padding: 18,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 18,
                  minWidth: 0,
                }}
              >
                <img
                  src={
                    eng.vendor?.profileImageUrl ||
                    "https://ui-avatars.com/api/?name=" +
                      encodeURIComponent(eng.vendor?.userFirstName || "V") +
                      "+" +
                      encodeURIComponent(eng.vendor?.userLastName || "N") +
                      "&background=6C63FF&color=fff&size=64"
                  }
                  alt="Vendor"
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #6C63FF22",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: "#222",
                      fontWeight: 700,
                      fontSize: 18,
                      marginBottom: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {eng.vendor?.userFirstName} {eng.vendor?.userLastName}
                  </div>
                  <div
                    style={{
                      color: "#6C63FF",
                      fontWeight: 600,
                      fontSize: 15,
                      marginBottom: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {eng.businessService?.businessName} &bull;{" "}
                    {eng.businessService?.serviceName}
                  </div>
                  <div
                    style={{
                      color: "#888",
                      fontSize: 15,
                      margin: "6px 0",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Status:{" "}
                    <span style={{ color: "#6C63FF", fontWeight: 700 }}>
                      {eng.status}
                    </span>
                  </div>
                  <div
                    style={{
                      color: "#555",
                      fontSize: 15,
                      fontStyle: "italic",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      maxWidth: 320,
                    }}
                  >
                    {eng.inquiryMessage}
                  </div>
                </div>
                <div
                  style={{
                    color: "#6C63FF",
                    fontWeight: 700,
                    fontSize: 15,
                    minWidth: 90,
                    textAlign: "right",
                    marginLeft: "auto",
                  }}
                >
                  {eng.serviceDate
                    ? new Date(eng.serviceDate).toLocaleDateString()
                    : ""}
                </div>
                <button
                  onClick={() => navigate(`/engagement-chat/${eng.id}`)}
                  style={{
                    background: "#6C63FF",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 18px",
                    fontWeight: 600,
                    fontSize: 15,
                    marginLeft: 18,
                    cursor: "pointer",
                  }}
                >
                  Go to Chat
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
